import { ErrorHandler, Injectable } from '@angular/core';

/**
 * Detects failures to load lazy-loaded JS chunks (dynamic imports) and reloads
 * the page once so the tab picks up the freshly deployed build.
 *
 * This happens when a new build is deployed while a tab still holds the old
 * index.html: the old app requests a chunk hash that no longer exists on the
 * host (503/404), and Angular throws "Failed to fetch dynamically imported
 * module". A single hard-reload fetches the new index.html and the current
 * chunk hashes.
 *
 * A sessionStorage guard throttles reloads: if the chunk is genuinely
 * unreachable (real, persistent 503), we reload at most once per cooldown
 * window instead of hammering the server, and let the error surface between
 * attempts.
 */
@Injectable()
export class ChunkLoadErrorHandler implements ErrorHandler {
  private static readonly RELOAD_GUARD_KEY = 'chunkReloadAt';
  private static readonly RELOAD_COOLDOWN_MS = 10_000;

  handleError(error: unknown): void {
    if (this.isChunkLoadError(error) && this.canReload()) {
      this.markReloaded();
      window.location.reload();
      return;
    }

    // Not a chunk error (or still inside the reload cooldown) — let it surface.
    console.error(error);
  }

  private isChunkLoadError(error: unknown): boolean {
    const message = this.messageOf(error);
    return (
      /Failed to fetch dynamically imported module/i.test(message) ||
      /error loading dynamically imported module/i.test(message) ||
      /Importing a module script failed/i.test(message)
    );
  }

  private messageOf(error: unknown): string {
    if (error instanceof Error) return error.message;
    // Angular sometimes wraps the original error under `.rejection` or `.cause`.
    const wrapped = error as { rejection?: unknown; cause?: unknown; message?: unknown };
    if (wrapped?.rejection) return this.messageOf(wrapped.rejection);
    if (wrapped?.cause) return this.messageOf(wrapped.cause);
    return typeof wrapped?.message === 'string' ? wrapped.message : String(error);
  }

  private canReload(): boolean {
    const last = Number(this.readGuard() ?? 0);
    return Date.now() - last > ChunkLoadErrorHandler.RELOAD_COOLDOWN_MS;
  }

  private markReloaded(): void {
    // sessionStorage can throw (private mode, storage disabled). A secondary
    // throw here would mask the original error, so swallow it and still reload.
    try {
      sessionStorage.setItem(ChunkLoadErrorHandler.RELOAD_GUARD_KEY, String(Date.now()));
    } catch {
      /* no-op: without the guard we may reload again, which is still recoverable */
    }
  }

  private readGuard(): string | null {
    try {
      return sessionStorage.getItem(ChunkLoadErrorHandler.RELOAD_GUARD_KEY);
    } catch {
      return null;
    }
  }
}
